import { useEffect, useState } from "react";
import { motion } from "@/lib/motion";
import {
    ArrowUpRight,
    AlertTriangle,
    GitBranch,
    Layers,
    Users,
    Compass,
    ShieldCheck,
    ServerCog,
    Cloud,
    Sparkles,
    DatabaseBackup,
    Fingerprint,
    Briefcase,
    Brain,
    Gauge,
    Boxes,
    TrendingUp,
    Target,
} from "lucide-react";
import "@/App.css";
import { ThemeProvider } from "@/context/ThemeContext";
import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";
import { Analytics } from "@/components/Analytics";
import { Reveal as _SharedReveal, Overline } from "@/components/Reveal";
import {
    Accordion,
    AccordionContent,
    AccordionItem,
    AccordionTrigger,
} from "@/components/ui/accordion";
import { getEnterprisePage } from "@/lib/sanityQueries";
import { peek } from "@/lib/sanity";
import { skipIntro } from "@/lib/prerender";

const ease = [0.22, 1, 0.36, 1];
const ext = (cta) =>
    cta?.openInNewTab ? { target: "_blank", rel: "noopener noreferrer" } : {};

// Mount-triggered reveal (same fade/rise motion as the shared Reveal) so every
// Enterprise section is visible in normal document flow regardless of scroll
// position or full-page capture — the shared whileInView variant left
// below-the-fold content at opacity:0 until scrolled into view.
const Reveal = ({ children, delay = 0, className = "" }) => (
    <motion.div
        initial={skipIntro() ? false : { opacity: 0, y: 24 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.7, delay, ease }}
        className={className}
    >
        {children}
    </motion.div>
);

// Frontend-owned icons, mapped by position.
const CHALLENGE_ICONS = [AlertTriangle, GitBranch, Layers, Users, Compass];
const OUTCOME_ICONS = [ShieldCheck, Boxes, TrendingUp, Target];
const SERVICE_ICONS = [ServerCog, ShieldCheck, Cloud, Compass, Sparkles, DatabaseBackup];
const DIFF_ICONS = [Fingerprint, Briefcase, Users, Brain, Gauge];

// Fallbacks (used only if Sanity is unavailable).
const FB = {
    heroEyebrow: "Enterprise Standards for Growing Businesses",
    heroHeadline: "Technology Leadership Without Enterprise Overhead.",
    heroSupportingMessage:
        "Managed IT, cybersecurity, cloud solutions, and strategic technology guidance designed to reduce risk, improve operational maturity, and support business growth.",
    heroPrimaryCta: { label: "Schedule a Conversation", url: "#", openInNewTab: false },
    heroSecondaryCta: { label: "Explore Our Services", url: "/services", openInNewTab: false },
    challengeHeadline: "Growth Creates New Technology Demands",
    challengeIntro:
        "As organizations grow, technology becomes more important to operations, security, productivity, and customer experience.",
    businessChallenges: [
        { _key: "c1", title: "Hidden Technology Risk", description: "Security, access, and operational gaps can remain invisible until something breaks." },
        { _key: "c2", title: "Lack of Structure", description: "Onboarding, access management, and governance are often inconsistent or informal." },
        { _key: "c3", title: "Reactive Technology Management", description: "Teams spend too much time responding to problems instead of preventing them." },
        { _key: "c4", title: "Limited Internal Resources", description: "Enterprise-level expertise without the cost of a full internal IT department." },
        { _key: "c5", title: "Technology Misalignment", description: "Decisions made without considering impact on operations, risk, and growth." },
    ],
    outcomesHeadline: "Build a Stronger Technology Foundation",
    businessOutcomes: [
        { _key: "o1", title: "More Secure", description: "Reduce technology risk through stronger security practices and proactive protection." },
        { _key: "o2", title: "More Structured", description: "Consistent processes, documentation, governance, and accountability." },
        { _key: "o3", title: "More Scalable", description: "A technology environment that supports new employees, locations, and growth." },
        { _key: "o4", title: "Better Aligned", description: "Technology decisions connected to business priorities and strategic goals." },
    ],
    servicesHeadline: "Technology Services Built Around Business Outcomes",
    servicesIntro:
        "Managed operations, cybersecurity, cloud infrastructure, and strategic guidance for a more mature technology environment.",
    featuredServices: [
        { _id: "s1", name: "Managed IT & Operational Excellence", shortDescription: "Proactive monitoring, support, and operational processes that keep your business moving." },
        { _id: "s2", name: "Cybersecurity & Risk Protection", shortDescription: "Managed safeguards across identities, endpoints, email, networks, and backups." },
        { _id: "s3", name: "Cloud, Infrastructure & Scalability", shortDescription: "Secure cloud and resilient infrastructure for collaboration, continuity, and growth." },
        { _id: "s4", name: "IT Strategy, Advisory & vCIO Services", shortDescription: "Strategic guidance connecting IT decisions to business goals and budget." },
        { _id: "s5", name: "Digital Experience & Custom Solutions", shortDescription: "Custom integrations and workflow automation that simplify how teams work." },
        { _id: "s6", name: "Business Continuity & Resilience", shortDescription: "Continuity ownership that keeps your business operational, whatever happens." },
    ],
    differentiatorHeadline: "More Than Traditional IT Support",
    differentiatorMessage:
        "We work as a trusted technology partner focused on risk reduction, operational maturity, and long-term client success.",
    differentiators: [
        { _key: "d1", title: "Enterprise Standards", description: "Enterprise-grade tools and operational discipline adapted for growing organizations." },
        { _key: "d2", title: "Business-First Thinking", description: "We focus on the business impact of technology decisions." },
        { _key: "d3", title: "Trusted Partnership", description: "An extension of your leadership team, not a transactional vendor." },
        { _key: "d4", title: "Human Expertise", description: "Judgment, accountability, and experience remain essential." },
        { _key: "d5", title: "Proactive Approach", description: "Reduce risk and create stability before problems become disruptions." },
    ],
    featuredFaqs: [
        { _id: "f1", question: "Does EasiLinked replace our existing IT team?", answer: "Not necessarily. We can operate as your managed technology partner or work alongside existing internal resources." },
        { _id: "f2", question: "Is our organization too small for managed IT and cybersecurity?", answer: "Growing businesses face many of the same risks as larger organizations, often with fewer resources." },
        { _id: "f3", question: "Why invest in professional managed IT services?", answer: "Downtime, security incidents, and lost productivity can create far greater business costs." },
        { _id: "f4", question: "How is EasiLinked different from traditional reactive IT support?", answer: "We focus on prevention, operational maturity, and business outcomes—not just resolving tickets." },
    ],
    finalCtaHeadline: "Technology Should Support Growth, Not Create Risk.",
    finalCtaMessage:
        "Let's talk about your current environment and what a stronger technology foundation could look like.",
    finalCta: { label: "Schedule a Conversation", url: "#", openInNewTab: false },
};

const useEnterpriseSeo = (seo) => {
    useEffect(() => {
        if (!seo) return;
        const prevTitle = document.title;
        if (seo.metaTitle) document.title = seo.metaTitle;

        const upsertMeta = (attr, key, value) => {
            if (!value) return null;
            let el = document.head.querySelector(`meta[${attr}="${key}"]`);
            const created = !el;
            if (!el) {
                el = document.createElement("meta");
                el.setAttribute(attr, key);
                document.head.appendChild(el);
            }
            el.setAttribute("content", value);
            return { el, created };
        };

        const desc = upsertMeta("name", "description", seo.metaDescription);
        const robots = seo.noIndex ? upsertMeta("name", "robots", "noindex, nofollow") : null;

        let canonical = null;
        if (seo.canonicalUrl) {
            canonical = document.head.querySelector('link[rel="canonical"]') || null;
            if (!canonical) {
                canonical = document.createElement("link");
                canonical.setAttribute("rel", "canonical");
                document.head.appendChild(canonical);
            }
            canonical.setAttribute("href", seo.canonicalUrl);
        }

        return () => {
            document.title = prevTitle;
            if (robots?.created) robots.el.remove();
            if (canonical) canonical.remove();
            // description meta is left in place (commonly present site-wide)
        };
    }, [seo]);
};

const CtaLink = ({ cta, fallbackLabel, variant = "primary", testid }) => {
    const label = cta?.label || fallbackLabel;
    const url = cta?.url || "#";
    const base =
        "group inline-flex items-center gap-2 rounded-control px-7 py-3.5 text-sm font-semibold transition-colors duration-150";
    const styles =
        variant === "primary"
            ? "bg-brand-accent text-brand-on-accent"
            : "border-[1.5px] border-brand-control-border text-brand-text hover:bg-brand-surface2";
    return (
        <a href={url} {...ext(cta)} data-testid={testid} className={`${base} ${styles}`}>
            {label}
            <ArrowUpRight className="h-4 w-4 transition-colors duration-150" />
        </a>
    );
};

const EnterpriseContent = () => {
    const [data, setData] = useState(() => peek(getEnterprisePage));
    useEffect(() => {
        let active = true;
        getEnterprisePage()
            .then((res) => {
                if (active && res) setData(res);
            })
            .catch(() => {});
        return () => {
            active = false;
        };
    }, []);

    useEnterpriseSeo(data?.seo);

    const d = data || {};
    const challenges = d.businessChallenges?.length ? d.businessChallenges : FB.businessChallenges;
    const outcomes = d.businessOutcomes?.length ? d.businessOutcomes : FB.businessOutcomes;
    const svcs = d.featuredServices?.length ? d.featuredServices : FB.featuredServices;
    const diffs = d.differentiators?.length ? d.differentiators : FB.differentiators;
    const faqs = d.featuredFaqs?.length ? d.featuredFaqs : FB.featuredFaqs;

    return (
        <div className="min-h-screen bg-brand-bg font-body text-brand-text antialiased">
            <Navbar />
            <main>
                {/* 1. HERO */}
                <section
                    id="top"
                    data-testid="enterprise-hero"
                    className="relative overflow-hidden pt-36 pb-20 sm:pt-44 sm:pb-24"
                >
                    <div className="pointer-events-none absolute inset-0 el-grid-bg" aria-hidden />
                    <div className="relative mx-auto max-w-4xl px-5 text-center sm:px-8">
                        <Reveal>
                            <span className="inline-flex items-center">
                                <Overline>{d.heroEyebrow || FB.heroEyebrow}</Overline>
                            </span>
                        </Reveal>
                        <Reveal delay={0.05}>
                            <h1
                                data-testid="enterprise-hero-heading"
                                className="mt-7 font-heading text-4xl font-extrabold leading-[1.02] tracking-tight text-brand-text sm:text-6xl"
                            >
                                {d.heroHeadline || FB.heroHeadline}
                            </h1>
                        </Reveal>
                        <Reveal delay={0.1}>
                            <p className="mx-auto mt-6 max-w-2xl text-base leading-relaxed text-brand-muted sm:text-lg">
                                {d.heroSupportingMessage || FB.heroSupportingMessage}
                            </p>
                        </Reveal>
                        <Reveal delay={0.15}>
                            <div className="mt-9 flex flex-wrap items-center justify-center gap-3">
                                <CtaLink
                                    cta={d.heroPrimaryCta}
                                    fallbackLabel={FB.heroPrimaryCta.label}
                                    variant="primary"
                                    testid="enterprise-hero-primary-cta"
                                />
                                <CtaLink
                                    cta={d.heroSecondaryCta}
                                    fallbackLabel={FB.heroSecondaryCta.label}
                                    variant="secondary"
                                    testid="enterprise-hero-secondary-cta"
                                />
                            </div>
                        </Reveal>
                    </div>
                </section>

                {/* 2. BUSINESS CHALLENGES */}
                <section
                    data-testid="enterprise-challenges"
                    className="mx-auto max-w-7xl px-5 py-24 sm:px-8 sm:py-28"
                >
                    <div className="max-w-2xl">
                        <Reveal>
                            <Overline>The Business Reality</Overline>
                        </Reveal>
                        <Reveal delay={0.05}>
                            <h2 className="mt-5 font-heading text-4xl font-extrabold leading-[1.02] tracking-tight text-brand-text sm:text-5xl">
                                {d.challengeHeadline || FB.challengeHeadline}
                            </h2>
                        </Reveal>
                        <Reveal delay={0.1}>
                            <p className="mt-6 text-base leading-relaxed text-brand-muted">
                                {d.challengeIntro || FB.challengeIntro}
                            </p>
                        </Reveal>
                    </div>
                    <div className="mt-12 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
                        {challenges.map((c, i) => {
                            const Icon = CHALLENGE_ICONS[i] || CHALLENGE_ICONS[CHALLENGE_ICONS.length - 1];
                            return (
                                <Reveal key={c._key || i} delay={(i % 3) * 0.06}>
                                    <motion.div
                                        whileHover={{ y: -4 }}
                                        transition={{ duration: 0.3 }}
                                        className="h-full rounded-2xl border border-brand-line bg-brand-surface p-6 transition-colors duration-150 hover:border-brand-accent/40"
                                    >
                                        <span className="grid h-12 w-12 place-items-center rounded-xl border border-brand-line bg-brand-bg text-brand-accent">
                                            <Icon className="h-5 w-5" />
                                        </span>
                                        <h3 className="mt-5 font-heading text-lg font-bold tracking-tight text-brand-text">
                                            {c.title}
                                        </h3>
                                        <p className="mt-2 text-sm leading-relaxed text-brand-muted">
                                            {c.description}
                                        </p>
                                    </motion.div>
                                </Reveal>
                            );
                        })}
                    </div>
                </section>

                {/* 3. BUSINESS OUTCOMES */}
                <section
                    data-testid="enterprise-outcomes"
                    className="border-y border-brand-line bg-brand-surface py-24 sm:py-28"
                >
                    <div className="mx-auto max-w-7xl px-5 sm:px-8">
                        <Reveal>
                            <Overline>Outcomes</Overline>
                        </Reveal>
                        <Reveal delay={0.05}>
                            <h2 className="mt-5 max-w-2xl font-heading text-4xl font-extrabold leading-[1.02] tracking-tight text-brand-text sm:text-5xl">
                                {d.outcomesHeadline || FB.outcomesHeadline}
                            </h2>
                        </Reveal>
                        <div className="mt-12 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4">
                            {outcomes.map((o, i) => {
                                const Icon = OUTCOME_ICONS[i] || OUTCOME_ICONS[OUTCOME_ICONS.length - 1];
                                return (
                                    <Reveal key={o._key || i} delay={i * 0.06}>
                                        <div className="h-full rounded-2xl border border-brand-line bg-brand-bg p-6">
                                            <div className="flex items-center justify-between">
                                                <span className="font-heading text-3xl font-extrabold tracking-tight text-brand-accent">
                                                    {`0${i + 1}`}
                                                </span>
                                                <Icon className="h-5 w-5 text-brand-muted" />
                                            </div>
                                            <h3 className="mt-5 font-heading text-lg font-bold tracking-tight text-brand-text">
                                                {o.title}
                                            </h3>
                                            <p className="mt-2 text-sm leading-relaxed text-brand-muted">
                                                {o.description}
                                            </p>
                                        </div>
                                    </Reveal>
                                );
                            })}
                        </div>
                    </div>
                </section>

                {/* 4. ENTERPRISE SERVICES */}
                <section
                    id="services"
                    data-testid="enterprise-services"
                    className="mx-auto max-w-7xl px-5 py-24 sm:px-8 sm:py-28"
                >
                    <div className="mb-12 max-w-2xl">
                        <Reveal>
                            <Overline>Capabilities</Overline>
                        </Reveal>
                        <Reveal delay={0.05}>
                            <h2 className="mt-5 font-heading text-4xl font-extrabold leading-[1.02] tracking-tight text-brand-text sm:text-5xl">
                                {d.servicesHeadline || FB.servicesHeadline}
                            </h2>
                        </Reveal>
                        <Reveal delay={0.1}>
                            <p className="mt-6 text-base leading-relaxed text-brand-muted">
                                {d.servicesIntro || FB.servicesIntro}
                            </p>
                        </Reveal>
                    </div>
                    <div className="grid grid-cols-1 gap-5 md:grid-cols-2 lg:grid-cols-3">
                        {svcs.map((s, i) => {
                            const Icon = SERVICE_ICONS[i] || SERVICE_ICONS[SERVICE_ICONS.length - 1];
                            return (
                                <Reveal key={s._id || i} delay={(i % 3) * 0.06}>
                                    <motion.div
                                        whileHover={{ y: -6 }}
                                        transition={{ duration: 0.35, ease }}
                                        data-testid={`enterprise-service-card-${i}`}
                                        className="group h-full rounded-3xl border border-brand-line bg-brand-surface p-7 transition-colors duration-150 hover:border-brand-accent/40"
                                    >
                                        <div className="flex items-start justify-between">
                                            <span className="grid h-14 w-14 place-items-center rounded-2xl border border-brand-line bg-brand-bg text-brand-accent transition-colors duration-150 group-hover:bg-brand-accent group-hover:text-brand-on-accent">
                                                <Icon className="h-6 w-6" />
                                            </span>
                                            <ArrowUpRight className="h-5 w-5 text-brand-muted opacity-0 transition-colors duration-150 group-hover:opacity-100" />
                                        </div>
                                        <h3 className="mt-6 font-heading text-xl font-bold tracking-tight text-brand-text">
                                            {s.name}
                                        </h3>
                                        <p className="mt-3 text-sm leading-relaxed text-brand-muted">
                                            {s.shortDescription}
                                        </p>
                                    </motion.div>
                                </Reveal>
                            );
                        })}
                    </div>
                </section>

                {/* 5. WHY EASILINKED / DIFFERENTIATORS */}
                <section
                    id="why"
                    data-testid="enterprise-differentiators"
                    className="border-y border-brand-line bg-brand-surface py-24 sm:py-28"
                >
                    <div className="mx-auto grid max-w-7xl grid-cols-1 gap-14 px-5 sm:px-8 lg:grid-cols-12 lg:items-start">
                        <div className="lg:col-span-4">
                            <Reveal>
                                <Overline>Why EasiLinked</Overline>
                            </Reveal>
                            <Reveal delay={0.05}>
                                <h2 className="mt-5 font-heading text-4xl font-extrabold leading-[1.02] tracking-tight text-brand-text sm:text-5xl">
                                    {d.differentiatorHeadline || FB.differentiatorHeadline}
                                </h2>
                            </Reveal>
                            <Reveal delay={0.1}>
                                <p className="mt-6 text-base leading-relaxed text-brand-muted">
                                    {d.differentiatorMessage || FB.differentiatorMessage}
                                </p>
                            </Reveal>
                        </div>
                        <div className="lg:col-span-8">
                            <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
                                {diffs.map((p, i) => {
                                    const Icon = DIFF_ICONS[i] || DIFF_ICONS[DIFF_ICONS.length - 1];
                                    return (
                                        <Reveal key={p._key || i} delay={i * 0.06}>
                                            <div className="h-full rounded-2xl border border-brand-line bg-brand-bg p-7">
                                                <span className="grid h-12 w-12 place-items-center rounded-xl border border-brand-line text-brand-accent">
                                                    <Icon className="h-5 w-5" />
                                                </span>
                                                <h3 className="mt-6 font-heading text-lg font-bold tracking-tight text-brand-text">
                                                    {p.title}
                                                </h3>
                                                <p className="mt-2 text-sm leading-relaxed text-brand-muted">
                                                    {p.description}
                                                </p>
                                            </div>
                                        </Reveal>
                                    );
                                })}
                            </div>
                        </div>
                    </div>
                </section>

                {/* 7. FAQs */}
                <section
                    data-testid="enterprise-faqs"
                    className="border-y border-brand-line bg-brand-surface py-24 sm:py-28"
                >
                    <div className="mx-auto max-w-3xl px-5 sm:px-8">
                        <Reveal>
                            <Overline>FAQ</Overline>
                        </Reveal>
                        <Reveal delay={0.05}>
                            <h2 className="mt-5 font-heading text-4xl font-extrabold leading-[1.02] tracking-tight text-brand-text sm:text-5xl">
                                Common Questions
                            </h2>
                        </Reveal>
                        <Reveal delay={0.1}>
                            <Accordion type="single" collapsible className="mt-10 w-full">
                                {faqs.map((f, i) => (
                                    <AccordionItem
                                        key={f._id || i}
                                        value={`faq-${i}`}
                                        data-testid={`enterprise-faq-${i}`}
                                        className="border-brand-line"
                                    >
                                        <AccordionTrigger className="text-left font-heading text-lg font-bold tracking-tight text-brand-text hover:no-underline">
                                            {f.question}
                                        </AccordionTrigger>
                                        <AccordionContent className="text-sm leading-relaxed text-brand-muted">
                                            {f.answer}
                                        </AccordionContent>
                                    </AccordionItem>
                                ))}
                            </Accordion>
                        </Reveal>
                    </div>
                </section>

                {/* 8. CLOSING CTA */}
                <section
                    data-testid="enterprise-closing-cta"
                    className="mx-auto max-w-7xl px-5 py-24 sm:px-8 sm:py-28"
                >
                    <div className="relative overflow-hidden rounded-card border border-brand-line bg-brand-surface px-6 py-20 text-center sm:px-16 sm:py-24">
                        <div className="pointer-events-none absolute inset-0 el-grid-bg opacity-40" aria-hidden />
                        <Reveal>
                            <span className="font-mono text-[11px] uppercase tracking-[0.28em] text-brand-accent">
                                Start the conversation
                            </span>
                        </Reveal>
                        <Reveal delay={0.05}>
                            <h2 className="mx-auto mt-6 max-w-3xl font-heading text-4xl font-extrabold leading-[1.0] tracking-tight text-brand-text sm:text-5xl">
                                {d.finalCtaHeadline || FB.finalCtaHeadline}
                            </h2>
                        </Reveal>
                        <Reveal delay={0.1}>
                            <p className="mx-auto mt-6 max-w-xl text-base leading-relaxed text-brand-muted">
                                {d.finalCtaMessage || FB.finalCtaMessage}
                            </p>
                        </Reveal>
                        <Reveal delay={0.15}>
                            <div className="mt-10 flex justify-center">
                                <CtaLink
                                    cta={d.finalCta}
                                    fallbackLabel={FB.finalCta.label}
                                    variant="primary"
                                    testid="enterprise-closing-cta-button"
                                />
                            </div>
                        </Reveal>
                    </div>
                </section>
            </main>
            <Footer />
            <Analytics />
        </div>
    );
};

export default function EnterprisePage() {
    return (
        <ThemeProvider>
            <EnterpriseContent />
        </ThemeProvider>
    );
}
